/**
 * 资料门（账号级资料未完善 → 强制先去填写）
 * -----------------------------------------------------------------------------
 * 套在 `RequireSession` **内层**：身份确认之后再判资料，两件事不混在一起 ——
 * 外层管「你是谁」（未授权 → 引导页 / 静默授权），本层管「你的资料齐没齐」。
 *
 * 判定只认服务端下发的 `profileCompleted`（姓名非空 + 已绑手机号），
 * 前端不自己拼条件 —— 否则「空串算不算没填」「全角空格算不算」这类问题
 * 会在三端各长出一个版本。
 *
 * 白名单（不拦）：
 *   - `/profile/complete` 填写页自身（否则无限重定向）
 *   - `/help/service` 联系客服：手机号验证依赖短信通道，短信出问题（未开通 / 欠费 /
 *     签名审核未过）时学员会卡在填写页。留一个能联系到人的出口，比「强制填完才能用」
 *     更重要 —— 没有出口的强制 = 用户彻底失联。
 *
 * 回跳：被拦时把原路径塞进 `?next=`，填完回原地。`next` 只接受站内相对路径，
 * 填写页会再校验一次（防开放重定向）。
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../api/hooks';
import { LoadingBlock } from './layout';

/** 不拦的路径（精确匹配前缀即放行） */
const ALLOW_PREFIXES = ['/profile/complete', '/help/service'];

function isAllowed(pathname: string): boolean {
  return ALLOW_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function RequireProfile({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data } = useSession();

  // 外层 `RequireSession` 已保证有 data；这里只是类型收窄，正常不会渲染到
  if (!data) return <LoadingBlock text="正在确认资料" />;

  if (data.profileCompleted || isAllowed(location.pathname)) return children;

  const next = `${location.pathname}${location.search}`;
  return <Navigate to={`/profile/complete?next=${encodeURIComponent(next)}`} replace />;
}

/**
 * 校验回跳目标：只放行站内绝对路径。
 *
 * `//evil.com` 与 `https://evil.com` 都必须挡掉 —— 前者在浏览器里会被当成
 * 协议相对 URL 直接跳外站。
 */
export function safeNextPath(raw: string | null): string {
  // 空值 / 非站内绝对路径 / 协议相对 URL（`//evil.com`，浏览器会当外站跳）一律回服务台
  if (raw === null || raw === '' || !raw.startsWith('/') || raw.startsWith('//')) return '/home';
  return raw;
}
